import React from "react";
import { useTranslation } from "react-i18next";
import { FieldArray, useFormikContext } from "formik";
import styled from "styled-components";

import {
  Button,
  ButtonTheme,
  Label,
  SelectField,
  TCreateAuctionFormValues,
} from "@components";
import { FormError } from "@components/FormError";
import { device } from "@styles/breakpoints";
import { ICountry, STAKEHOLDER_AGENCIES } from "@utils";

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 10rem;
  gap: 20px;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.tripleMargin};
  width: 100%;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const Title = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 12rem 12rem;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.tripleMargin};

  @media ${device.tablet} {
    grid-template-columns: 1fr;
    gap: 12px;
    justify-items: flex-start;
  }
`;

const TitleButtonContainer = styled.div`
  @media ${device.tablet} {
    width: 100%;

    & button {
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const Error = styled.div`
  margin-bottom: ${({ theme }) => theme.tripleMargin};
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;

  & button {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
  }
`;

type Props = {
  countries: ICountry[];
};

const EMPTY_STATE = {
  country_geoname_id: "",
  agency: "",
};

export const Stakeholders = ({ countries }: Props) => {
  const { t } = useTranslation();
  const {
    values: { stakeholders },
  } = useFormikContext<TCreateAuctionFormValues>();

  return (
    <>
      {countries && (
        <FieldArray
          name="stakeholders"
          render={(helpers) => (
            <>
              <Title>
                <Label>{t("auctionForm.stakeholderTitle")}</Label>
                <TitleButtonContainer>
                  <Button
                    type="button"
                    theme={ButtonTheme.Blue}
                    onClick={() => {
                      helpers.push(EMPTY_STATE);
                    }}
                  >
                    {t<string>("auctionForm.stakeholderAdd")}
                  </Button>
                </TitleButtonContainer>
              </Title>
              {stakeholders && (
                <>
                  {stakeholders.map((stakeholder, index) => (
                    <Row>
                      <SelectField
                        name={`stakeholders[${index}].country_geoname_id`}
                        placeholder={t("auctionForm.stakeholdersPlaceholder")}
                        options={countries.map((country) => ({
                          label: country.name,
                          value: country.geoname_id,
                        }))}
                      />
                      <SelectField
                        name={`stakeholders[${index}].agency`}
                        placeholder={t("auctionForm.stakeholderAgency")}
                        options={STAKEHOLDER_AGENCIES.map((agency) => ({
                          label: agency,
                          value: agency,
                        }))}
                      />
                      <Button
                        type="button"
                        disabled={stakeholders.length === 1}
                        theme={ButtonTheme.Quaternary}
                        onClick={() => {
                          helpers.remove(index);
                        }}
                        style={{ marginTop: "6px" }}
                      >
                        {t<string>("auctionForm.stakeholderDelete")}
                      </Button>
                    </Row>
                  ))}
                </>
              )}
            </>
          )}
        />
      )}
      <Error>
        <FormError name="deposits" />
      </Error>
    </>
  );
};
